import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import TranslateIcon from "@mui/icons-material/Translate";
import {Checkbox} from "@bounce/shared/ui";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";

type CheckboxState = "checked" | "indeterminate" | "unchecked";

type Props = {
  selectedCount: number;
  checkboxState: CheckboxState;
  onToggleAll: () => void;
  onActivate: () => void;
  onTranslate: () => void;
  onDelete: () => void;
};

const HEADER_HEIGHT = 44;

const CHECKBOX_COL_WIDTH = 50;

const barSx = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: HEADER_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: Colors.blue._100,
  borderBottom: `1px solid ${Colors.tertiary._300}`,
  zIndex: 3,
} as const;

const actionButtonSx = {
  height: 28,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  textTransform: "none",
  gap: 0.5,
  px: 1.5,
  border: `1px solid ${Colors.tertiary._300}`,
  backgroundColor: Colors.bw.white,
  color: Colors.grey._800,
  "&:hover": {
    backgroundColor: Colors.tertiary._100,
    borderColor: Colors.grey._500,
  },
} as const;

const deleteButtonSx = {
  ...actionButtonSx,
  "&:hover": {
    backgroundColor: Colors.red._100,
    borderColor: Colors.red._400,
    color: Colors.red._800,
  },
} as const;

export const TemplatesBatchActions = ({
  selectedCount,
  checkboxState,
  onToggleAll,
  onActivate,
  onTranslate,
  onDelete,
}: Props) => {
  const selectedLabel = `${selectedCount} selected`;

  return (
    <Box sx={barSx} role="toolbar" aria-live="polite">
      <Box sx={{display: "flex", alignItems: "center"}}>
        <Box
          sx={{
            width: CHECKBOX_COL_WIDTH,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Checkbox
            size="small"
            checked={checkboxState === "checked"}
            indeterminate={checkboxState === "indeterminate"}
            onChange={onToggleAll}
          />
        </Box>
        <Text size={13} variant="inter600" sx={{color: Colors.grey._800, pl: 1.5}}>
          {selectedLabel}
        </Text>
      </Box>
      <Box sx={{display: "flex", alignItems: "center", gap: 0.75, pr: 2.5}}>
        <Button
          buttonType="outlined-primary"
          size="small"
          onClick={onActivate}
          sx={actionButtonSx}
        >
          <CheckCircleOutlineIcon sx={{fontSize: 16}} />
          Activate
        </Button>
        <Button
          buttonType="outlined-primary"
          size="small"
          onClick={onTranslate}
          sx={actionButtonSx}
        >
          <TranslateIcon sx={{fontSize: 16}} />
          Translate
        </Button>
        <Button
          buttonType="outlined-primary"
          size="small"
          onClick={onDelete}
          sx={deleteButtonSx}
          aria-describedby="selection-count"
        >
          <DeleteOutlineRoundedIcon sx={{fontSize: 16}} />
          Delete
        </Button>
      </Box>
    </Box>
  );
};
